/**
 * Consentimiento de cookies (art. 22.2 LSSI, guía de cookies de la AEPD) y carga de Google Ads.
 *
 * Las únicas cookies publicitarias de la web son las de Google Ads, para medir qué anuncios traen
 * solicitudes de visita. Google Consent Mode v2 en modo básico: index.html lo deja todo denegado
 * por defecto y gtag.js no se descarga hasta que la persona acepta en el banner (CookieBanner).
 * Sin aceptar no sale ninguna petición hacia Google Ads.
 */

export const GOOGLE_ADS_ID = 'AW-17649407655';

export type Eleccion = 'aceptadas' | 'rechazadas';

interface Registro {
  eleccion: Eleccion;
  fecha: string;
  version: number;
}

const CLAVE = 'gandara-cookies';
/** Subirla si cambian las cookies o su finalidad: vuelve a preguntar a todo el mundo */
const VERSION = 1;
/** Pasado este plazo se vuelve a preguntar (la AEPD recomienda no más de 24 meses) */
export const VIGENCIA_MESES = 12;
const DIA_MS = 24 * 60 * 60 * 1000;
const VIGENCIA_MS = VIGENCIA_MESES * 30 * DIA_MS;
const EVENTO_AJUSTES = 'gandara:ajustes-cookies';
/** La elección cambió desde otra pestaña o al volver atrás: el banner se pone al día */
const EVENTO_CAMBIO = 'gandara:consentimiento';
/** Visitas que ya aceptaron: gtag.js se descarga al primer gesto o, como mucho, a los 5 s */
const ESPERA_DESCARGA_MS = 5000;
/** Parámetros con los que Google Ads marca la llegada desde un anuncio */
const CLIC_ANUNCIO = /[?&](gclid|gbraid|wbraid)=/;

// Solo si no se pudo guardar: la última elección de esta visita manda sobre lo que hubiera guardado
let enMemoria: Registro | null = null;
let configurado = false;
let descargado = false;
// URL de llegada (solo en memoria): si trae el clic del anuncio, la medición la necesita aunque
// la persona acepte más tarde en otra página
const urlEntrada = typeof window !== 'undefined' ? (window.location?.href ?? '') : '';

const vigente = (r: Partial<Registro> | null): r is Registro => {
  if (r?.version !== VERSION || (r.eleccion !== 'aceptadas' && r.eleccion !== 'rechazadas')) return false;
  const edad = Date.now() - Date.parse(r.fecha);
  // Un día de margen por relojes desajustados: una fecha en el futuro no puede valer para siempre
  return edad > -DIA_MS && edad < VIGENCIA_MS;
};

/** Elección vigente, o null si hay que preguntar */
export const leerConsentimiento = (): Eleccion | null => {
  if (enMemoria) return vigente(enMemoria) ? enMemoria.eleccion : null;
  try {
    const registro = JSON.parse(window.localStorage.getItem(CLAVE) ?? 'null');
    if (vigente(registro)) return registro.eleccion;
  } catch {
    // Sin almacenamiento o registro corrupto: se vuelve a preguntar
  }
  return null;
};

/**
 * Aceptada y con Google Ads ya activado en esta página. Solo entonces tiene sentido enviar una
 * conversión: en una pestaña que aún no lo aplicó se quedaría en cola sin consentimiento.
 */
export const publicidadAceptada = () => configurado && leerConsentimiento() === 'aceptadas';

// Solo medición de conversiones: sin publicidad personalizada (ad_personalization sigue denegado)
const actualizarGoogle = (valor: 'granted' | 'denied') =>
  window.gtag?.('consent', 'update', { ad_storage: valor, ad_user_data: valor });

const descargarGtag = () => {
  // Puede llegar tarde (gesto o reposo): si entretanto se retiró el consentimiento, no se descarga
  if (descargado || leerConsentimiento() !== 'aceptadas') return;
  descargado = true;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(s);
};

const activarGoogleAds = (inmediato: boolean) => {
  actualizarGoogle('granted');
  if (!configurado) {
    configurado = true;
    window.gtag?.('js', new Date());
    const conClic = CLIC_ANUNCIO.test(urlEntrada) && !CLIC_ANUNCIO.test(window.location.href);
    window.gtag?.('config', GOOGLE_ADS_ID, ...(conClic ? [{ page_location: urlEntrada }] : []));
    if (!inmediato) {
      // Solo para quien ya aceptó: la conexión lista para que la conversión no llegue tarde
      const preconexion = document.createElement('link');
      preconexion.rel = 'preconnect';
      preconexion.href = 'https://www.googletagmanager.com';
      document.head.appendChild(preconexion);
      // Como antes del banner: al primer gesto o en reposo, sin competir con la carga de la página
      ['scroll', 'click', 'touchstart', 'keydown'].forEach((e) =>
        document.addEventListener(e, descargarGtag, { once: true, passive: true }),
      );
      if ('requestIdleCallback' in window) window.requestIdleCallback(descargarGtag, { timeout: ESPERA_DESCARGA_MS });
      else setTimeout(descargarGtag, ESPERA_DESCARGA_MS);
    }
  }
  if (inmediato) descargarGtag();
};

/** Borra lo que Google Ads guarda en este dominio (cookies _gcl_* y sus copias en localStorage) */
const borrarCookiesGoogleAds = () => {
  const nombres = document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((n) => n.startsWith('_gcl_'));
  const partes = window.location.hostname.split('.');
  const dominios = ['', ...partes.slice(0, -1).map((_, i) => `; domain=.${partes.slice(i).join('.')}`)];
  for (const nombre of nombres) {
    for (const dominio of dominios) document.cookie = `${nombre}=; Max-Age=0; path=/${dominio}`;
  }
  try {
    for (const clave of Object.keys(window.localStorage)) {
      if (clave.startsWith('_gcl_')) window.localStorage.removeItem(clave);
    }
  } catch {
    // Sin almacenamiento: no hay nada que borrar
  }
};

/** La elección cambió fuera de esta página (otra pestaña, o vuelta atrás desde la caché) */
const sincronizar = () => {
  if (leerConsentimiento() === 'aceptadas') {
    activarGoogleAds(false);
  } else if (configurado) {
    actualizarGoogle('denied');
    borrarCookiesGoogleAds();
  }
  window.dispatchEvent(new Event(EVENTO_CAMBIO));
};

/** Al arrancar la web: si ya aceptó y sigue vigente, Google Ads se activa en diferido */
export const iniciarConsentimiento = () => {
  // En /admin no hay banner ni publicidad (y hay datos personales de las familias)
  if (typeof window === 'undefined' || window.location.pathname.startsWith('/admin')) return;
  if (leerConsentimiento() === 'aceptadas') activarGoogleAds(false);
  // Sin aceptar: fuera lo que dejó la web antes del banner, cuando Google Ads cargaba sin preguntar
  else borrarCookiesGoogleAds();
  window.addEventListener('storage', (e) => {
    if (e.key === CLAVE || e.key === null) sincronizar();
  });
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) sincronizar();
  });
};

/** Guarda la elección del banner y la aplica al momento */
export const guardarConsentimiento = (eleccion: Eleccion) => {
  const registro: Registro = { eleccion, fecha: new Date().toISOString(), version: VERSION };
  try {
    window.localStorage.setItem(CLAVE, JSON.stringify(registro));
    enMemoria = null;
  } catch {
    // No se pudo guardar: vale para esta visita, y lo guardado antes deja de valer
    enMemoria = registro;
    try {
      window.localStorage.removeItem(CLAVE);
    } catch {
      // Sin almacenamiento en absoluto
    }
  }
  if (eleccion === 'aceptadas') {
    activarGoogleAds(true);
  } else {
    // Retirar el consentimiento: Google deja de usar cookies desde ya y se borran las que dejó.
    // No se envían más conversiones (publicidadAceptada) aunque gtag.js siga cargado en la página
    actualizarGoogle('denied');
    borrarCookiesGoogleAds();
  }
};

/** Vuelve a abrir el banner (enlace «Configurar cookies» del pie) */
export const abrirAjustesCookies = () => window.dispatchEvent(new Event(EVENTO_AJUSTES));

export const alAbrirAjustesCookies = (cb: () => void) => {
  window.addEventListener(EVENTO_AJUSTES, cb);
  return () => window.removeEventListener(EVENTO_AJUSTES, cb);
};

export const alCambiarConsentimiento = (cb: () => void) => {
  window.addEventListener(EVENTO_CAMBIO, cb);
  return () => window.removeEventListener(EVENTO_CAMBIO, cb);
};
